import { ReactNode } from 'react';
import { t } from '@lingui/core/macro';
import { DOCUMENT_UPLOAD_UK } from 'components/Registration/FileUpload/FileUpload.helpers';
import { useIntroducerData } from 'contexts/IntroducerData/IntroducerData.context';
import { useLocale } from 'hooks/useLocale';
import { useGetIntroducerDocuments } from 'lendingApi/lendingApiHooks';
import * as yup from 'yup';
import { TRegistrationDocumentField } from './RegistrationDocuments.types';

type TDocumentConfig = {
  docType: string;
  field: TRegistrationDocumentField;
  label: ReactNode;
  instruction: ReactNode;
  requiredMessage: string;
  brokerOnly?: boolean;
};

export const useRegistrationDocuments = () => {
  const { introducerId, isContextLoading, introducerCompany, introducerType } =
    useIntroducerData();
  const { isDE } = useLocale();
  const { data: introducerDocuments, isLoading: isLoadingIntroducerDocuments } =
    useGetIntroducerDocuments(introducerId);

  const UK_DOCUMENTS: TDocumentConfig[] = [
    {
      docType: 'bank_statement',
      field: 'bankStatementFile',
      label: DOCUMENT_UPLOAD_UK.bank_statement.label,
      instruction: DOCUMENT_UPLOAD_UK.bank_statement.instruction,
      requiredMessage: 'Bank statement is required',
    },
    {
      docType: 'certified_id',
      field: 'certifiedIdFile',
      label: DOCUMENT_UPLOAD_UK.certified_id.label,
      instruction: DOCUMENT_UPLOAD_UK.certified_id.instruction,
      requiredMessage: 'Certified ID is required',
    },
    {
      docType: 'proof_of_address',
      field: 'proofOfAddressFile',
      label: DOCUMENT_UPLOAD_UK.proof_of_address.label,
      instruction: DOCUMENT_UPLOAD_UK.proof_of_address.instruction,
      requiredMessage: 'Proof of business address is required',
    },
  ];

  const DE_DOCUMENTS: TDocumentConfig[] = [
    {
      docType: 'broker_questionnaire',
      field: 'brokerQuestionnaireFile',
      label: t`Partner questionnaire`,
      instruction: t`Upload the fully completed and signed questionnaire.`,
      requiredMessage: t`Partner questionnaire is required`,
    },
    {
      docType: 'hr_trade_certificate',
      field: 'hrTradeCertificateFile',
      label: t`Current commercial register extract / trade licence`,
      instruction: t`Upload the commercial register extract or trade licence.`,
      requiredMessage: t`Commercial register extract or trade licence is required`,
    },
    {
      docType: 'license_34c',
      field: 'license34cFile',
      label: t`§34c GewO (loan brokering)`,
      instruction: t`Upload your official trade permit under §34c(1) sentence 1 no. 2 GewO.`,
      requiredMessage: t`§34c GewO permit is required`,
      brokerOnly: true,
    },
    {
      docType: 'certified_id',
      field: 'certifiedIdFile',
      label: t`ID card`,
      instruction: t`Upload both sides of your official photo ID (passport or ID card) as a single file.`,
      requiredMessage: t`ID card is required`,
    },
  ];

  const documents = (isDE ? DE_DOCUMENTS : UK_DOCUMENTS).filter(
    (doc) => !doc.brokerOnly || introducerType === 'broker',
  );

  const uploadedDocTypes: string[] =
    introducerDocuments?.data.map((doc) => doc.document_type) ?? [];

  const missingDocuments = documents.filter(
    (doc) => !uploadedDocTypes.includes(doc.docType),
  );

  const schema = yup
    .object()
    .shape(
      Object.fromEntries(
        missingDocuments.map((doc) => [
          doc.field,
          yup.mixed().required(doc.requiredMessage),
        ]),
      ),
    );

  return {
    schema,
    introducerId,
    introducerAccountId: introducerCompany?.id,
    isLoading: isContextLoading || isLoadingIntroducerDocuments,
    missingDocuments,
  };
};
